// The conversation.
//
// One thread — "whichever open thread you participate in", chosen in `snapshot.ts` from
// what the server said this caller participates in and never from the plan's state.
//
// Read back through everything, paginated on sequence: "Read older" asks for one more page
// and re-reads the lot. There is no infinite scroller, because an infinite scroller in a
// thread somebody is reading for the fourth time to find the sentence where their mother
// changed her mind is a way of losing your place.
//
// Pending actions are interleaved by timestamp. See `plan/inline.ts`.
//
// What is not here: no editing, no deleting, no reactions, no read receipts, no typing
// indicator, no presence, no "jump to latest" badge that counts. Unread state IS built —
// see `plan/useMarkThreadRead.ts` and the tab badge in `PlanScreen.tsx` — but it is
// self-facing only: nobody sees another participant's read position, which is what "no read
// receipts" above still means. The facilitator's own state is one line and is in
// `FacilitatorState`.

import { Composer } from '../components/Composer.js';
import { ThreadRoster } from '../components/ThreadRoster.js';
import type { Internals } from '../internals.js';
import { interleave } from '../plan/inline.js';
import type { PlanState } from '../plan/usePlan.js';
import { useFollowBottom } from '../plan/useFollowBottom.js';
import { useMarkThreadRead } from '../plan/useMarkThreadRead.js';

export type ConversationProps = {
  state: PlanState;
  internals: Internals;
  /** The mobile decisions strip (`DecisionsMobileStrip`, built in `PlanScreen.tsx`),
   * rendered directly above the composer so it is actually above it in the document —
   * not merely positioned there with CSS over a sibling laid out beside the conversation.
   * `null` renders nothing; this component knows nothing else about decisions. */
  decisionsStrip?: JSX.Element | null;
};

export function Conversation({
  state,
  internals,
  decisionsStrip,
}: ConversationProps): JSX.Element | null {
  const snapshot = state.snapshot;

  // `useFollowBottom` is a hook, so it has to be called on every render this component
  // makes, including the ones before a snapshot exists — the null check below can't come
  // first. `lastKey` comes from the interleaved list, not the raw messages, so a pending
  // action arriving with no accompanying message still moves it. `resetKey` is the thread
  // being read; see `useFollowBottom` for why that, and not the plan.
  //
  // Somebody else's ask has no `createdAt` (see `inline.ts`), so it always sits at
  // `items[items.length - 1]` for as long as it stays open — the last item's own key
  // never changes while a message arrives behind it. The count is included so that a
  // message landing anywhere in the thread still moves `lastKey`, trailing ask or not.
  const actions =
    snapshot === null
      ? []
      : snapshot.pendingActions.filter(
          (action) => action.threadId === snapshot.thread?.id,
        );
  const items = snapshot === null ? [] : interleave(snapshot.messages, actions);
  const lastKey = `${items.length}:${items[items.length - 1]?.key ?? ''}`;
  const { scroller, atBottom } = useFollowBottom(lastKey, snapshot?.thread?.id ?? '');
  useMarkThreadRead(
    snapshot?.thread?.id ?? null,
    snapshot?.messages ?? [],
    atBottom,
    state.markRead,
  );

  if (snapshot === null) {
    return null;
  }

  const { MessageRow, PendingActionRow, FacilitatorState } = internals;
  const open = snapshot.thread?.acceptingMessages ?? false;

  return (
    <section className="conversation" aria-label="Conversation">
      <ThreadRoster people={snapshot.people} kind="shared" />
      <div className="conversation__scroller" ref={scroller}>
        {snapshot.nextBefore === null ? null : (
          <button
            type="button"
            className="button conversation__older"
            onClick={state.loadOlder}
          >
            Read older
          </button>
        )}
        {items.length === 0 ? (
          <p className="conversation__empty">
            Nothing has been said yet. Start wherever you like — Mor will follow.
          </p>
        ) : (
          <ol className="conversation__items">
            {items.map((item) =>
              item.kind === 'message' ? (
                <MessageRow
                  key={item.key}
                  message={item.message}
                  people={snapshot.people}
                />
              ) : (
                <PendingActionRow
                  key={item.key}
                  action={item.action}
                  people={snapshot.people}
                  resolve={state.resolve}
                />
              ),
            )}
          </ol>
        )}
      </div>

      <div className="conversation__foot">
        <FacilitatorState facilitatorBusy={snapshot.plan.facilitatorBusy} />
        {decisionsStrip ?? null}
        {open ? (
          <Composer
            posting={state.posting}
            sendFailed={state.sendFailed}
            onSend={state.post}
          />
        ) : (
          // A closed thread is a finished conversation, not a broken one. The record stays
          // readable; that is the whole of the `done` state as far as this screen goes.
          <p className="conversation__closed">
            This conversation is closed. Everything said in it stays here to read.
          </p>
        )}
      </div>
    </section>
  );
}
